import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStepEffects } from "@/hooks/useAuthStepEffects";
import { useNavigate, useLocation } from "react-router-dom";
import { MButton } from "@/components/ui/atoms/MButtons";
import { isValidEmail, isValidMobile } from "@/utils";
import MInputField from "@/components/ui/atoms/MInputField";
import { useAuth } from "@/hooks/useAuth";
import { useDialCode } from "@/hooks/useDialCode";
import MDialCode from "@/components/ui/molecules/MDialCode";
import { dialCodeOptions } from "@/utils/countryCodes";
import MOnboardingHeader from "@/components/ui/organisms/MOnboardingHeader";
import { getPreferredLoginMethod } from "@/utils/storage-utils";
import { useAccountCheck } from "@/hooks/useAccountCheck";
import { MDialog } from "@/components/ui/atoms/MDialog";
import { IconAlertTriangle } from "@tabler/icons-react";

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [otpValue, setOtpValue] = useState("");
  const [step, setStep] = useState<"input" | "otp">("input");
  const [showDialCodeDrawer, setShowDialCodeDrawer] = useState(false);

  const preferredMethod = getPreferredLoginMethod();
  const loginMode = location.state?.mode || preferredMethod || "mobile";
  const isLoginFlow = location.state?.isLogin || true; // Default to true for login form
  const autoSendOtp = location.state?.autoSendOtp as boolean | undefined;
  const [autoSending, setAutoSending] = useState(Boolean(autoSendOtp));
  const prefillEmail = location.state?.prefillEmail as string | undefined;
  const prefillMobile = location.state?.prefillMobile as string | undefined;
  const prefillDialCode = location.state?.prefillDialCode as string | undefined;
  const autoOtpSentRef = useRef(false);

  // useAuth for login logic
  const {
    sendOtp,
    verifyOtp,
    loading,
    inputValue,
    setInputValue,
    setOtp,
    setOtpSent,
    setError,
    setVerifyType,
  } = useAuth();

  // Unified hook for auth effects (verifyType, resets, resend timer)
  // Adapter for setVerifyType to accept 'sms' | 'email' and convert to 'mobile' | 'email'
  const setVerifyTypeAdapter = (v: "sms" | "email") =>
    setVerifyType(v === "sms" ? "mobile" : "email");

  // useDialCode for country code
  const { dialCode, setDialCode } = useDialCode("+91");

  // Use unified hook for all OTP/resend/CTA logic
  const { resendTimer, handleResend, handleCta } = useAuthStepEffects({
    mode: loginMode,
    setVerifyType: setVerifyTypeAdapter,
    setStep,
    setOtpSent,
    setError,
    setOtpValue,
    setInputValue,
    setOtp,
    step,
    registerError: undefined,
    progress: undefined,
    setProgress: undefined,
    sendOtp,
    verifyOtp,
    inputValue,
    dialCode,
    otpValue,
    isSignup: false,
  });

  // Set verifyType once on mount
  useEffect(() => {
    setVerifyType(loginMode === "mobile" ? "mobile" : "email");
  }, [loginMode, setVerifyType]);

  // Pre-fill contact when navigated from "Continue to login" in the already-registered dialog
  useEffect(() => {
    if (!autoSendOtp) return;
    if (prefillEmail) {
      setInputValue(prefillEmail);
      setVerifyType("email");
    } else if (prefillMobile) {
      setInputValue(prefillMobile);
      setVerifyType("mobile");
      if (prefillDialCode) setDialCode(prefillDialCode);
    }
  }, []);

  // Auto-send OTP once inputValue is populated from the pre-fill above
  useEffect(() => {
    if (
      !autoSendOtp ||
      autoOtpSentRef.current ||
      !inputValue ||
      step !== "input"
    )
      return;
    autoOtpSentRef.current = true;
    handleCta().finally(() => setAutoSending(false));
  }, [inputValue]);

  const [showNoAccountDialog, setShowNoAccountDialog] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const { check: checkAccount, loading: preCheckLoading } = useAccountCheck();

  // Helper: navigate to consent screen with pre-filled contact and auto-send passthrough
  const redirectToSignupOtp = () => {
    navigate("/register/consent", {
      state: {
        mode: loginMode,
        fromLogin: true,
        autoSendOtp: true,
        prefillMobile: loginMode === "mobile" ? inputValue : undefined,
        prefillEmail: loginMode === "email" ? inputValue : undefined,
        prefillDialCode: loginMode === "mobile" ? dialCode : undefined,
      },
    });
  };

  // Pre-check before sending login OTP: only registered users can log in.
  // For unregistered contacts, check for pending invites and route appropriately.
  const preCheckAndProceed = async () => {
    try {
      const contact =
        loginMode === "mobile" ? { mobile: inputValue } : { email: inputValue };
      const { status } = await checkAccount(contact);

      if (status === "registered") {
        handleCta();
        return;
      }

      if (status === "has-invites") {
        // Has pending invite(s) — go to signup OTP; SignupConfirm presents invite options
        redirectToSignupOtp();
        return;
      }

      // status === 'new' — no account, no invites
      setShowNoAccountDialog(true);
    } catch {
      // On unexpected error fall back to handleCta; server error will surface naturally
      handleCta();
    }
  };

  const handleInputFocus = () => {
    setIsKeyboardOpen(true);
  };

  // Handle input blur to scroll back to top when keyboard closes
  const handleInputBlur = () => {
    setTimeout(() => {
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA");

      if (!isInputFocused && isKeyboardOpen) {
        setIsKeyboardOpen(false);
        // Commented out scroll-to-top functionality
        // window.scrollTo({ top: 0, behavior: 'smooth' });
        // const scrollContainer = document.querySelector('.flex-1.min-h-0.overflow-y-auto');
        // if (scrollContainer) {
        //   scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        // }
      }
    }, 150);
  };

  return (
    <React.Fragment>
      <div
        className="w-full min-h-screen bg-white flex flex-col"
        style={{ height: "100vh", maxHeight: "100vh" }}
      >
        {/* Onboarding Header with gradient, back/exit, and icon */}
        <MOnboardingHeader
          onBack={() => navigate(-1)}
          onExit={() => navigate("/")}
          showIcon={loginMode === "mobile" ? "phone" : "email"}
        />

        <div
          className="flex-1 min-h-0 overflow-y-auto px-6 mt-8 pb-6"
          style={{
            WebkitOverflowScrolling: "touch",
            overflowY: "auto",
            flex: 1,
            paddingBottom: "50vh",
          }}
        >
          {step === "input" && !autoSending && (
            <>
              {/* Title */}
              <h1 className="text-[24px] font-bold text-[#36116D] mb-2">
                {loginMode === "mobile"
                  ? t("auth.onboarding.signupForm.titleMobile")
                  : t("auth.onboarding.signupForm.titleEmail")}
              </h1>
              <p className="text-[#474747] text-[16px] font-medium mb-8">
                {t("auth.onboarding.signupForm.subtitle")}
              </p>

              {/* Input Section */}
              {loginMode === "mobile" ? (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center gap-2 px-4 py-3 border-2 border-[#6BA2EA] rounded-lg">
                      {(() => {
                        const codeObj = dialCodeOptions.find(
                          (opt) => opt.value === dialCode
                        );
                        return codeObj ? (
                          <img
                            src={`https://flagcdn.com/w40/${codeObj.countryCode.toLowerCase()}.png`}
                            alt={codeObj.countryCode}
                            className="w-6 h-4"
                          />
                        ) : null;
                      })()}
                      <span className="font-semibold text-gray-900">
                        {dialCode}
                      </span>
                    </div>
                    <MInputField
                      size="xl"
                      variant="Outlined"
                      state="Default"
                      inputMode="numeric"
                      type="number"
                      maxLength={10}
                      placeholder={t(
                        "auth.onboarding.signupForm.mobilePlaceholder"
                      )}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      className="flex-1"
                    />
                  </div>
                  <div className="text-center mb-8">
                    <button
                      className="text-[#45168C] font-semibold text-sm"
                      onClick={() =>
                        navigate("/login/form", {
                          state: { mode: "email", isLogin: true },
                        })
                      }
                    >
                      {t("auth.onboarding.signupForm.useEmailInstead")}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <MInputField
                    size="xl"
                    variant="Outlined"
                    type="email"
                    placeholder={t(
                      "auth.onboarding.signupForm.emailPlaceholder"
                    )}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    className="mb-6"
                    state="Default"
                  />

                  <div className="text-center mb-8">
                    <button
                      className="text-[#45168C] font-semibold text-sm"
                      onClick={() =>
                        navigate("/login/form", {
                          state: { mode: "mobile", isLogin: true },
                        })
                      }
                    >
                      {t("auth.onboarding.signupForm.useMobileInstead")}
                    </button>
                  </div>
                </>
              )}

              {/* Button inside scrollable area with extra padding */}
              <div
                className="mt-6 confirm-footer"
                style={{ paddingBottom: "2rem" }}
              >
                <MButton
                  variant="filled"
                  size="lg"
                  className="w-full"
                  onClick={preCheckAndProceed}
                  disabled={
                    !inputValue ||
                    loading ||
                    preCheckLoading ||
                    (loginMode === "mobile"
                      ? !isValidMobile(inputValue)
                      : !isValidEmail(inputValue))
                  }
                >
                  {t("auth.onboarding.signupForm.confirm")}
                </MButton>
              </div>
            </>
          )}
          {step === "otp" && (
            <>
              {/* Title */}
              <h1 className="text-[24px] font-bold text-purple-900 mb-2">
                {loginMode === "mobile"
                  ? t("auth.onboarding.signupForm.titleMobile")
                  : t("auth.onboarding.signupForm.titleEmail")}
              </h1>
              <p className="text-gray-600 otpText mb-8">
                {t("auth.onboarding.signupForm.otpSent", {
                  contact:
                    loginMode === "mobile"
                      ? `${dialCode} ${inputValue}`
                      : inputValue,
                })}
              </p>

              {/* OTP Input */}
              <MInputField
                size="xl"
                variant="Outlined"
                state="Default"
                inputMode="numeric"
                type="number"
                maxLength={6}
                placeholder={t("auth.onboarding.signupForm.otpPlaceholder")}
                value={otpValue}
                autoFocus
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setOtpValue(val);
                }}
                className="mb-4"
              />

              {/* Resend code link */}
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm text-gray-600">
                  {t("auth.onboarding.signupForm.didntGetCode")}
                </span>
                <button
                  style={{ color: "#B23F83" }}
                  className="text-sm font-semibold disabled:opacity-50"
                  disabled={resendTimer > 0 || loading}
                  onClick={() => {
                    if (resendTimer === 0 && !loading) handleResend();
                  }}
                >
                  {t("auth.onboarding.signupForm.resendCode")}
                </button>
              </div>

              {resendTimer > 0 && (
                <p className="text-sm text-gray-500 mb-8">
                  {t("auth.onboarding.signupForm.resendIn", {
                    seconds: resendTimer.toString().padStart(2, "0"),
                  })}
                </p>
              )}

              {/* Button inside scrollable area with extra padding */}
              <div
                className="mt-6 confirm-footer"
                style={{ paddingBottom: "2rem" }}
              >
                <MButton
                  variant="filled"
                  size="lg"
                  className="w-full"
                  onClick={handleCta}
                  disabled={otpValue.length !== 6 || loading}
                >
                  {t("auth.onboarding.signupForm.confirm")}
                </MButton>
              </div>
            </>
          )}
        </div>
      </div>
      <MDialog
        isOpen={showNoAccountDialog}
        onClose={() => setShowNoAccountDialog(false)}
        title={t("auth.signupLanding.noAccountDialog.title")}
        description={t("auth.signupLanding.noAccountDialog.description")}
        secondaryCtaText={t("auth.signupLanding.noAccountDialog.exit")}
        onSecondaryCta={() => {
          setShowNoAccountDialog(false);
          navigate("/signup");
        }}
        primaryCtaText={t("auth.signupLanding.noAccountDialog.createAccount")}
        showIconVariant
        iconNoBackground
        icon={<IconAlertTriangle size={64} color="#45168C" />}
        onPrimaryCta={() => {
          setShowNoAccountDialog(false);
          navigate("/register/info", {
            state: {
              fromLogin: true,
              autoSendOtp: true,
              prefillMobile: loginMode === "mobile" ? inputValue : undefined,
              prefillEmail: loginMode === "email" ? inputValue : undefined,
              prefillDialCode: loginMode === "mobile" ? dialCode : undefined,
              mode: loginMode,
            },
          });
        }}
      />
    </React.Fragment>
  );
};

export default LoginForm;
